import { z } from 'zod';
import { PRIORITIES, TASK_STATUSES } from '../constants';
import { objectIdSchema, optionalDate, optionalObjectId, optionalText, paginationQuerySchema, patchOf, tagsSchema } from './common';

export const checklistItemSchema = z.object({
  text: z.string().trim().min(1).max(200),
  done: z.boolean().default(false),
});

export const taskCreateSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: optionalText(5000),
  priority: z.enum(PRIORITIES).default('medium'),
  category: optionalText(60),
  assigneeIds: z.array(objectIdSchema).max(20).default([]),
  departmentId: optionalObjectId(),
  startDate: optionalDate(),
  dueAt: optionalDate(),
  estimatedHours: z.coerce.number().min(0).max(10000).nullable().optional(),
  requiresApproval: z.boolean().default(false),
  checklist: z.array(checklistItemSchema).max(50).default([]),
  tags: tagsSchema,
  related: z
    .object({ type: z.enum(['lead', 'client', 'project']), id: objectIdSchema })
    .nullable()
    .optional(),
});
export type TaskCreateInput = z.input<typeof taskCreateSchema>;

export const taskUpdateSchema = patchOf(
  taskCreateSchema.omit({ assigneeIds: true, checklist: true, related: true }).extend({
    actualHours: z.coerce.number().min(0).max(10000).nullable().optional(),
  }),
);
export type TaskUpdateInput = z.input<typeof taskUpdateSchema>;

export const taskAssignSchema = z.object({
  assigneeIds: z.array(objectIdSchema).max(20),
});

export const taskStatusSchema = z.object({
  status: z.enum(TASK_STATUSES),
  note: optionalText(1000),
});

export const taskChecklistToggleSchema = z.object({
  done: z.boolean(),
});

export const taskChecklistAddSchema = checklistItemSchema.pick({ text: true });

export const taskCommentSchema = z.object({
  body: z.string().trim().min(1).max(4000),
});

export const taskReviewSchema = z.object({
  decision: z.enum(['approve', 'reopen']),
  note: optionalText(1000),
});

export const taskListQuerySchema = paginationQuerySchema.extend({
  view: z.enum(['my', 'assigned_by_me', 'team', 'all']).default('my'),
  status: z.union([z.enum(TASK_STATUSES), z.literal('open')]).optional(),
  priority: z.enum(PRIORITIES).optional(),
  due: z.enum(['today', 'overdue', 'upcoming']).optional(),
  assigneeId: objectIdSchema.optional(),
  departmentId: objectIdSchema.optional(),
  relatedType: z.enum(['lead', 'client', 'project']).optional(),
  relatedId: objectIdSchema.optional(),
});
export type TaskListQuery = z.input<typeof taskListQuerySchema>;
